import React, { useState } from "react";
import { FaTrash, FaEdit, FaKey } from "react-icons/fa";
import { formatDate } from "../../utils/dateUtils.js";
import classNames from "classnames";
import { useAuth } from "../../contexts/AuthContextProvider.jsx";
import { register, resetPassword } from "../../services/authService.js";
import { showPromiseToast } from "../../services/toastService.js";
import { deleteAllSessions, deleteUser, updateUser } from "../../services/userService.js";
import { HiOutlineLogout } from "react-icons/hi";
import ConfirmModal from "../modals/ConfirmModal.jsx";
import { LEVEL_MAP } from "../../utils/usersUtil.js";

const UserManagement = ({ state, setState, setIsDirty }) => {
	const [formState, setFormState] = useState({
		id: null,
		username: "",
		email: "",
		level: 3,
		office: "",
		last_login: "",
	});
	const [isEditing, setIsEditing] = useState(false);
	const [showConfirmModal, setShowConfirmModal] = useState(false);
	const [confirmingPasswordReset, setConfirmingPasswordReset] = useState(false);
	const [selectedUserId, setSelectedUserId] = useState(null);
	const [confirmingSessionDeletion, setConfirmingSessionDeletion] = useState(false);
	const { user: curUser } = useAuth();

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormState({ ...formState, [name]: value });
		setIsDirty(true);
	};

	const resetForm = () => {
		setFormState({
			id: null,
			username: "",
			level: 3,
			office: "",
			email: "",
			last_login: "",
		});
		setIsEditing(false);
		setIsDirty(false);
	};

	const handleCreateOrUpdate = () => {
		if (isEditing) {
			showPromiseToast(
				updateUser(formState.id, formState.email, formState.level, formState.office),
				{
					pending: "Aggiornamento utente in corso...",
					success: "Utente aggiornato con successo",
				}
			).then(() => {
				const updatedUsers = state.map((user) =>
					user.id === formState.id
						? {
								...user,
								level: formState.level,
								office: formState.office,
								email: formState.email,
						  }
						: user
				);
				setState(updatedUsers);
				resetForm();
			});
		} else {
			const newUser = {
				username: formState.username,
				password: formState.password,
				level: formState.level,
				email: formState.email,
				office: formState.office,
				last_login: null,
				active_sessions: 0,
			};
			showPromiseToast(
				register(formState.username, formState.email, formState.level, formState.office),
				{
					pending: "Creazione utente in corso...",
					success: "Utente creato con successo",
				}
			).then((response) => {
				setState([...state, { ...newUser, id: response.data.id }]);
				resetForm();
			});
		}
	};

	const handleEditClick = (u) => {
		if (u.id !== curUser.id) {
			setFormState(u);
			setIsEditing(true);
		}
	};

	const handlePasswordEditClick = (user) => {
		setSelectedUserId(user.id);
		setConfirmingPasswordReset(true);
		setShowConfirmModal(true);
	};
	const confirmResetPassword = () => {
		showPromiseToast(resetPassword(selectedUserId), {
			pending: "Reset password in corso...",
			success: "Password resettata con successo",
		}).finally(() => {
			setShowConfirmModal(false);
			setConfirmingPasswordReset(false);
			setSelectedUserId(null);
		});
	};

	const confirmDeleteSessions = () => {
		showPromiseToast(deleteAllSessions(selectedUserId), {
			pending: "Eliminazione sessioni in corso...",
			success: "Sessioni eliminate con successo",
		})
			.then(() =>
				setState(
					state.map((user) =>
						user.id === selectedUserId ? { ...user, active_sessions: 0 } : user
					)
				)
			)
			.finally(() => {
				setShowConfirmModal(false);
				setConfirmingSessionDeletion(false);
				setSelectedUserId(null);
			});
	};

	const handleDeleteSessionsClick = (user) => {
		setSelectedUserId(user.id);
		setConfirmingSessionDeletion(true);
		setShowConfirmModal(true);
	};

	const handleDeleteClick = (id) => {
		setSelectedUserId(id);
		setShowConfirmModal(true);
	};

	const confirmDeleteUser = () => {
		showPromiseToast(deleteUser(selectedUserId), {
			pending: "Eliminazione utente in corso...",
			success: "Utente eliminato con successo",
		})
			.then(() => setState(state.filter((user) => user.id !== selectedUserId)))
			.finally(() => {
				setShowConfirmModal(false);
				setSelectedUserId(null);
			});
	};

	const cancelDeleteAction = () => {
		setShowConfirmModal(false);
		setConfirmingSessionDeletion(false);
		setSelectedUserId(null);
	};

	return (
		<div className="">
			<h2 className="text-xl md:text-2xl font-bold mb-4 text-center">Gestione Utenti</h2>

			{/* Form per Creazione/Modifica */}
			<div className="mb-6 p-4 bg-white shadow rounded-lg max-w-[100%]">
				<h3 className="text-lg font-semibold mb-4">
					{isEditing ? "Modifica Utente" : "Crea Nuovo Utente"}
				</h3>
				<div className="grid grid-cols-1 md:grid-cols-4 gap-4">
					<input
						type="text"
						name="username"
						placeholder="Username"
						value={formState.username}
						disabled={isEditing}
						onChange={handleInputChange}
						className={classNames("border border-gray-300 p-2 rounded ", {
							"cursor-not-allowed bg-gray-100": isEditing,
						})}
					/>
					<input
						type="email"
						name="email"
						placeholder="Email"
						value={formState.email}
						onChange={handleInputChange}
						className={"border border-gray-300 p-2 rounded "}
					/>
					<select
						name="level"
						value={formState.level}
						onChange={handleInputChange}
						className={"border border-gray-300 p-2 rounded  bg-white"}
					>
						{Object.entries(LEVEL_MAP).map(([key, label]) => (
							<option key={key} value={key}>
								{label}
							</option>
						))}
					</select>
					<input
						type="text"
						name="office"
						placeholder="Ente"
						value={formState.office}
						onChange={handleInputChange}
						className="border border-gray-300 p-2 rounded "
					/>
				</div>
				<div className="mt-4 flex justify-end">
					<button
						id="run"
						aria-label="Run"
						onClick={handleCreateOrUpdate}
						className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 mr-2"
					>
						{isEditing ? "Salva Modifiche" : "Crea Utente"}
					</button>
					{isEditing && (
						<button
							id="cancel"
							aria-label="Cancel"
							onClick={resetForm}
							className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
						>
							Annulla
						</button>
					)}
				</div>
			</div>

			{/* Tabella Utenti */}
			<div className="overflow-x-auto w-full">
				<table className="w-full border-collapse border border-gray-300">
					<thead>
						<tr className="bg-gray-200">
							<th className="border border-gray-300 p-2">ID</th>
							<th className="border border-gray-300 p-2">
								Username
								<br />
							</th>
							<th className="border border-gray-300 p-2">Livello</th>
							<th className="border border-gray-300 p-2">Ente</th>
							<th className="border border-gray-300 p-2">Ultimo Login</th>
							<th className="border border-gray-300 p-2">Sessioni</th>
							<th className="border border-gray-300 p-2">Azione</th>
						</tr>
					</thead>
					<tbody>
						{state.map((user) => (
							<tr key={user.id} className="hover:bg-gray-200 text-center">
								<td className="border border-gray-300 p-2">{user.id}</td>
								<td className="border border-gray-300 p-2">
									{user.username}
									<br />
									<span className="italic text-sm">{user.email}</span>
								</td>
								<td className="border border-gray-300 p-2">
									{LEVEL_MAP[user.level] || "N/A"}
								</td>
								<td className="border border-gray-300 p-2">{user.office}</td>
								<td className="border border-gray-300 p-2">
									{formatDate(user.last_login) || "N/A"}
								</td>
								<td className="border border-gray-300 p-2">
									{user.active_sessions}
								</td>
								<td className="border border-gray-300 p-2">
									{user.id === curUser.id ? (
										<span className="text-gray-500">Non disponibile</span>
									) : (
										<>
											<button
												id="edit"
												aria-label="Edit"
												onClick={() => handleEditClick(user)}
												className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 mr-2"
											>
												<FaEdit />
											</button>
											<button
												id="password"
												aria-label="Change Password"
												onClick={() => handlePasswordEditClick(user)}
												className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600 mr-2"
											>
												<FaKey />
											</button>
											{user.active_sessions > 0 && (
												<button
													id="sessions"
													aria-label="Delete Sessions"
													onClick={() => handleDeleteSessionsClick(user)}
													className="bg-orange-500 text-white px-3 py-1 rounded hover:bg-yellow-600 mr-2"
												>
													<HiOutlineLogout />
												</button>
											)}
											<button
												id="delete"
												aria-label="Delete"
												onClick={() => handleDeleteClick(user.id)}
												className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
											>
												<FaTrash />
											</button>
										</>
									)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{/* Modale di Conferma */}
			{showConfirmModal && (
				<ConfirmModal
					message={
						confirmingSessionDeletion
							? "Sei sicuro di voler eliminare tutte le sessioni di questo utente?"
							: confirmingPasswordReset
							? "Sei sicuro di voler resettare la password di questo utente?"
							: "Sei sicuro di voler eliminare questo utente?"
					}
					onConfirm={
						confirmingSessionDeletion
							? confirmDeleteSessions
							: confirmingPasswordReset
							? confirmResetPassword
							: confirmDeleteUser
					}
					onCancel={cancelDeleteAction}
				/>
			)}
		</div>
	);
};

export default UserManagement;
